import { useEffect, useState } from 'react';
import './odometer.sass';

type OdometerProps = {
  value: number | string;
  digits: number;
};

type OdometerNumberProps = {
  number: number;
};

const getRange = (start: number, end: number) => {
  const range = (end - start + 10) % 10;
  return range === 0 ? 10 : range;
};

function getRanges(start: number, end: number): number[] {
  // Ensure the start and end numbers are within the range of 0-9
  if (start < 0 || start > 9 || end < 0 || end > 9) {
    throw new Error('Start and end numbers must be between 0 and 9 inclusive');
  }

  const range = getRange(start, end);

  return new Array(range).fill(0).map((_, i) => (i + start + 1) % 10);
}

function OdometerNumber({ number }: OdometerNumberProps) {
  const duration = 1000;
  const [animationKey, setAnimationKey] = useState(false);
  const [strip, setStrip] = useState<number[]>([]);

  useEffect(() => {
    setStrip((currentStrip) => {
      const last = currentStrip?.slice(-1)[0];
      const newValues = last ? getRanges(last, number) : [number];

      return [...currentStrip, ...newValues];
    });

    const timeout = setTimeout(() => {
      setStrip([number]);
      setAnimationKey((current) => !current);
    }, duration);

    return () => clearTimeout(timeout);
  }, [number]);

  const style = {
    transition: `transform ${duration}ms`,
    transform: `translateY(${strip.length > 1 ? (strip.length - 1) * -1 : 0}em)`,
  };

  return (
    <div className="odometer__digit">
      <div key={animationKey.toString()} style={style} className="odometer__digit-track">
        {strip.map((digit, i) => (
          <div key={`digit-${i}`}>{digit}</div>
        ))}
      </div>
    </div>
  );
}

export default function Odometer({ value, digits }: OdometerProps) {
  const paddedValue = value.toString().padStart(digits, '0').split('').reverse();

  return (
    <div className="odometer">
      {paddedValue.map((number, i) => (
        <OdometerNumber key={i} number={Number(number)} />
      ))}
    </div>
  );
}
